import React, { useCallback } from 'react';
import {
  Button,
  Card,
  createStyles,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  withStyles
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import WarningIcon from '@material-ui/icons/Warning';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import LinkIcon from '@material-ui/icons/Link';
import DvrIcon from '@material-ui/icons/Dvr';

import {
  CustomInputEvent,
  ProductsWithSRP,
  Quotation,
  QuotationCategory,
  QuotationProduct
} from 'src/types';
import { copyToClipboard, formatCurrency, openInNewTab } from 'src/utils';
import { QuotationTotalPrice } from './QuotationTotalPrice';
import { QuotationQuantityCell } from './QuotationQuantityCell';
import { QuotationProductCell } from './QuotationProductCell';
import { QuotationCategoryCell } from './QuotationCategoryCell';
import { QuotationPromoPriceCell } from './QuotationPromoPriceCell';
import { QuotationSRPCell } from './QuotationSRPCell';
import { SRPOption } from 'src/types/srp-options';
//TODO: fix later to allow image on quotation selected products
// import QuotationProductImageCell from './QuotationProductImageCell';

interface QuotationMainTableProps {
  items: Quotation[];
  branchId?: number;
  srpValue?: SRPOption;
  showCleanSrpQuote: boolean;
  isSrpLoading?: boolean;
  showSRP: boolean;
  totalSRP?: number;
  productsWithSRP: ProductsWithSRP;
  hasCompatibility: boolean;
  categoryOptions: QuotationCategory[];
  productOptions: QuotationProduct[];
  isCategoryLoading?: boolean;
  onRemoveItem: (i: number) => void;
  onClearVoucherRow: (i: number) => void;
  onChangeQuantity: (e: CustomInputEvent, index: number) => void;
  onCustomCategInputChange: (e: CustomInputEvent, index: number) => void;
  onCustomProductInputChange: (e: CustomInputEvent, index: number) => void;
  onCustomPriceInputChange: (e: CustomInputEvent, index: number) => void;
  onChangeCategory: (val: QuotationCategory | null, index: number) => void;
  onChangeProduct: (val: QuotationProduct | null, index: number) => void;
}

const useStyles = makeStyles(() => ({
  root: {},
  table: {
    backgroundSize: '70px 60px',
    backgroundRepeat: 'repeat',
    background:
      'linear-gradient(rgba(255,255,255,.95), rgba(255,255,255,.95)), url("/static/images/avatars/pcworth_logo.png")'
  },
  amount: { fontWeight: 500 }
}));

const StyledTableRow = withStyles((theme) =>
  createStyles({
    root: {
      '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.disabledBackground
      }
    }
  })
)(TableRow);

export const MainTable = ({
  items,
  branchId,
  srpValue,
  showSRP,
  isSrpLoading,
  totalSRP,
  categoryOptions,
  productOptions,
  showCleanSrpQuote,
  productsWithSRP,
  hasCompatibility,
  isCategoryLoading,
  onRemoveItem,
  onClearVoucherRow,
  onChangeQuantity,
  onChangeCategory,
  onChangeProduct,
  onCustomCategInputChange,
  onCustomProductInputChange,
  onCustomPriceInputChange
}: QuotationMainTableProps) => {
  const classes = useStyles();

  const onCopyText = (text: string) => {
    const newText = `${text}`;
    copyToClipboard(newText);
  };

  const handleAmountPrice = useCallback(
    (p: QuotationProduct, qty: number) => {
      const { retail_price } = p;

      if (showSRP && p?.product_id) {
        const productIdIndexInSrp = productsWithSRP.findIndex(
          (srpProduct) => srpProduct?.product_id === p?.product_id
        );
        if (productIdIndexInSrp > -1) {
          return productsWithSRP?.[productIdIndexInSrp]?.total_srp || 0;
        }
      }

      if (showSRP && !p?.product_id && p?.product_name && p?.retail_price) {
        // Meaning custom input / pre order to. yung kulay blue na text.
        // TODO: Add quantity and custom: boolean here in filter for edge case scenarios
        const productIdIndexInSrp = productsWithSRP.findIndex(
          (srpProduct) =>
            !srpProduct?.product_id &&
            srpProduct?.product_name === p?.product_name &&
            srpProduct?.retail_price === p?.retail_price
        );

        if (productIdIndexInSrp > -1) {
          return productsWithSRP?.[productIdIndexInSrp]?.total_srp || 0;
        }
      }

      return !retail_price ? 0 : retail_price * (qty || 1); // computation for total amount handled locally
    },
    [showSRP, productsWithSRP]
  );

  const handleSRP = useCallback(
    (p: QuotationProduct) => {
      if (showSRP && p?.product_id) {
        const productIdIndexInSrp = productsWithSRP.findIndex(
          (srpProduct) => srpProduct?.product_id === p?.product_id
        );
        if (productIdIndexInSrp > -1) {
          return productsWithSRP?.[productIdIndexInSrp]?.new_srp || 0;
        }
      }

      if (showSRP && !p?.product_id && p?.product_name && p?.retail_price) {
        // Meaning custom input / pre order to. yung kulay blue na text.
        // TODO: Add quantity and custom: boolean here in filter for edge case scenarios
        const productIdIndexInSrp = productsWithSRP.findIndex(
          (srpProduct) =>
            !srpProduct?.product_id &&
            srpProduct?.product_name === p?.product_name &&
            srpProduct?.retail_price === p?.retail_price
        );

        if (productIdIndexInSrp > -1) {
          return productsWithSRP?.[productIdIndexInSrp]?.new_srp || 0;
        }
      }

      return 0;
    },
    [showSRP, productsWithSRP]
  );

  return (
    <Card>
      <Table className={classes.table} size="small">
        <TableHead>
          <TableRow>
            {hasCompatibility ? null : <TableCell>QTY</TableCell>}
            <TableCell>TYPE</TableCell>
            {/* TODO: fix later to add image quotation column */}
            {/* <TableCell>IMAGE</TableCell> */}
            <TableCell>PRODUCT</TableCell>
            <TableCell id="quotation-action-cell" />
            {!showCleanSrpQuote ? (
              <TableCell>🔥 PROMO PRICE 🔥</TableCell>
            ) : null}
            {showSRP ? <TableCell>STANDARD PRICE</TableCell> : null}
            <TableCell>{showSRP ? 'AMOUNT (SRP)' : 'AMOUNT'}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item: Quotation, i) => (
            <StyledTableRow hover key={i}>
              {hasCompatibility ? null : (
                <QuotationQuantityCell
                  data={item}
                  index={i}
                  hasCompatibility={hasCompatibility}
                  onChangeQuantity={onChangeQuantity} // mark this
                />
              )}
              <QuotationCategoryCell
                index={i}
                item={item}
                hasCompatibility={hasCompatibility}
                categoryOptions={categoryOptions}
                onChangeCategory={onChangeCategory}
                customInput={item?.product?.category_name || ''}
                onCustomCategInputChange={onCustomCategInputChange}
                isCategoryLoading={isCategoryLoading}
              />
              {/*TODO: fix later to allow image on quotation selected products */}
              {/* <QuotationProductImageCell item={item}/> */}
              <QuotationProductCell
                index={i}
                branchId={branchId}
                item={item}
                showSRP={showSRP}
                productOptions={productOptions}
                customProductInput={item?.product?.product_name || ''}
                onCustomProductInputChange={onCustomProductInputChange}
                onChangeProduct={onChangeProduct}
                isCategoryLoading={isCategoryLoading}
                onClearVoucherRow={onClearVoucherRow}
              />
              <TableCell id="quotation-action-cell">
                {item?.product?.product_name &&
                item?.product?.category_name !== 'VOUCHER' ? (
                  <Tooltip title="Copy">
                    <Button
                      style={{ padding: 0, margin: -15 }}
                      startIcon={<FileCopyIcon />}
                      onClick={() =>
                        onCopyText(item?.product?.product_name || '')
                      }
                    />
                  </Tooltip>
                ) : null}
                {item?.product?.product_name &&
                item?.product?.category_name !== 'VOUCHER' ? (
                  <Tooltip title="View product in ecomm">
                    <Button
                      style={{ padding: 0, margin: -15 }}
                      startIcon={<LinkIcon />}
                      onClick={() =>
                        openInNewTab(
                          `https://pcworth.com/product/${item?.product?.slug}`
                        )
                      }
                    />
                  </Tooltip>
                ) : null}
                {item?.product?.product_name &&
                item?.product?.category_name !== 'VOUCHER' ? (
                  <Tooltip title="View product in internal">
                    <Button
                      style={{ padding: 0, margin: -15 }}
                      startIcon={<DvrIcon />}
                      onClick={() =>
                        openInNewTab(
                          `/app/products/${item?.product?.product_id}`
                        )
                      }
                    />
                  </Tooltip>
                ) : null}
              </TableCell>
              {!showCleanSrpQuote ? (
                <QuotationPromoPriceCell
                  index={i}
                  item={item}
                  customPriceInput={
                    item?.product?.retail_price?.toString() || ''
                  }
                  onCustomPriceInputChange={onCustomPriceInputChange}
                />
              ) : null}
              {showSRP ? (
                <QuotationSRPCell srp={handleSRP(item.product)} />
              ) : null}
              <TableCell className={classes.amount}>
                {formatCurrency(handleAmountPrice(item.product, item.quantity))}
              </TableCell>
              <TableCell
                id="remove_row_icon"
                onClick={(e) => e.stopPropagation()}
              >
                {isCategoryLoading ? null : (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveItem(i);
                    }}
                  >
                    <ClearIcon color="secondary" fontSize="small" />
                  </IconButton>
                )}
              </TableCell>
              {item?.warning_note ? (
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Tooltip title={item?.warning_note || ''}>
                    <IconButton
                      disabled={!item?.warning_note}
                      size="small"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <WarningIcon color="secondary" fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              ) : null}
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
      <QuotationTotalPrice
        srpValue={srpValue}
        isSrpLoading={isSrpLoading}
        items={items}
        totalAmount={totalSRP}
        showSRP={showSRP}
        showCleanSrpQuote={showCleanSrpQuote}
      />
    </Card>
  );
};

export const QuotationMainTable = React.memo(MainTable);
